import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    Alert, Box, Grid, MenuItem, TextField,
    FormControl, FormHelperText, FormLabel, RadioGroup, FormControlLabel, Radio
} from "@mui/material";

import { AISLES_LAYER, EDITABLE_LAYERS, ZONES_LAYER } from "../tools/useLayout";
import { DRAWING_WAYS, getTypeParamError, ORIENTATIONS } from "../tools/useLayoutAnnotations";
import { isPlainObject, isValidJson, validateAlphanumeric } from "../tools/utils";
import CircularLoader from "../tools/CircularLoader";
import CircleColor from "./CircleColor";

export default forwardRef(function DialogEditAisleSide({
    data,
    modifiedLayout,
    colissionLayers,
    editAnnotation,
    onDisabledChange,
    linearTypes = [],
    linearTypeParams = {},
    isLoadingLinearTypes = false,
    getLinearTypes,
}, ref) {
    // Setup
    const { t } = useTranslation();

    // State initializers (must precede useState since defaultValues is used as initial state)
    const originalValues = useMemo(() => modifiedLayout?.[AISLES_LAYER]?.[data?.parent]?.[data?.name] || {}, [data, modifiedLayout]);

    const parsedName = useMemo(() => {
        const [aisle, orientation, part = null] = data?.name?.split('-');
        return { aisle, orientation, part };
    }, [data]);
    const defaultValues = useMemo(() => ({
        aisle: parsedName?.aisle || '',
        orientation: parsedName?.orientation || '',
        part: parsedName?.part || '',
        params: isPlainObject(originalValues?.params) ? JSON.stringify(originalValues?.params) : isValidJson(originalValues?.params) ? JSON.stringify(JSON.parse(originalValues.params), null, 2) : '{}',
        clientName: originalValues?.client_name || '',
        linearType: originalValues?.linear_type || 'aisle',
        zone: originalValues?.zone || '',
    }), [parsedName, originalValues]);

    // State
    const [formState, setFormState] = useState(defaultValues);
    const [errors, setErrors] = useState({});

    // Zones the aisle_side line overlaps, sorted by coverage (largest first).
    // Lets the user fix the zone of a side that straddles two zones.
    const zoneCollisions = useMemo(() => {
        const points = originalValues?.map_points;
        if (!points || points.length < 2) return [];
        return colissionLayers(ZONES_LAYER, { start: points[0], end: points[1] }, DRAWING_WAYS.LINE, 'partial');
    }, [originalValues, colissionLayers]);

    // Derived values
    const typeParamError = useMemo(
        () => getTypeParamError(formState.linearType, formState.params, linearTypeParams),
        [formState.linearType, formState.params, linearTypeParams]
    );

    const isSaveDisabled = !!typeParamError || !linearTypes.length;

    // Effects
    // Retries fetching the aisle types if a previous request failed
    useEffect(() => {
        getLinearTypes?.();
    }, [getLinearTypes]);

    useEffect(() => {
        onDisabledChange?.(isSaveDisabled);
    }, [isSaveDisabled, onDisabledChange]);

    // Default to zoneCollisions[0] (largest overlap) — must match the backend
    // criterion so the default equals what the robot gets. A persisted/manual
    // zone is never overwritten.
    useEffect(() => {
        if (zoneCollisions?.length > 0) {
            setFormState(prev => (prev.zone ? prev : { ...prev, zone: zoneCollisions[0].layer }));
        }
    }, [zoneCollisions]);

    // Functions
    const validateField = useCallback((field, value) => {
        const rules = {
            params: { mustBeJson: true },
            linearType: { required: true },
            clientName: { isAlphanumericWithSymbols: true },

        };
        const rule = rules?.[field];
        if (!rule) return null;
        if (rule.mustBeJson && !isValidJson(value))
            return t('overseer_app.layouts.creation.parameters_helper', 'This field must be a valid JSON object');
        if (rule.required && (!value || value === ''))
            return t('overseer_app.layouts.edition.default_error', 'This field is required');
        if (rule && rule.isAlphanumericWithSymbols && value) {  // Alphanumeric with symbols logic
            if (!validateAlphanumeric(value, { allowSymbols: true })) {
                return t('overseer_app.layouts.helper.client_name_helper', 'Only letters, numbers, underscores (_), and hyphens (-) are allowed.');
            }
        }
        return null;
    }, [t]);

    const handleFieldChange = useCallback((field, value) => {
        setErrors(prev => ({ ...prev, [field]: validateField(field, value) || undefined }));
        setFormState(prev => {
            const next = { ...prev, [field]: value };
            if (field === 'linearType') {
                try {
                    const parsed = JSON.parse(prev.params || '{}');
                    const prevConfig = linearTypeParams[prev.linearType];
                    if (prevConfig) delete parsed[prevConfig.key];
                    const nextConfig = linearTypeParams[value];
                    if (nextConfig) parsed[nextConfig.key] = nextConfig.value;
                    next.params = JSON.stringify(parsed, null, 2);
                } catch { /* invalid params JSON, skip injection */ }
            }
            return next;
        });
    }, [validateField, linearTypeParams]);

    const validateAllFields = useCallback(() => {
        const errors = {};
        Object.keys(formState).forEach(field => {
            const error = validateField(field, formState[field]);
            if (error) errors[field] = error;
        });
        setErrors(errors);
        if (Object.keys(errors).length > 0) return false;
        return true;
    }, [formState, validateField]);

    const onSave = useCallback(() => {
        const isValid = validateAllFields();
        if (!isValid) return null;
        return {
            needConfirm: false,
            callback: () => {
                editAnnotation(EDITABLE_LAYERS.AISLE_SIDE, {
                    _originalName: data?.name,
                    params: JSON.stringify(JSON.parse(formState?.params)),
                    client_name: formState?.clientName,
                    linear_type: formState?.linearType,
                    zone: formState?.zone || null,
                    parent: data?.parent,
                });
            },
        }
    }, [validateAllFields, editAnnotation, data, formState]);

    useImperativeHandle(ref, () => ({
        callbackOnSave: onSave,
    }));

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}></Grid>
            <Grid item xs={3}>
                <TextField label={t('overseer_app.layouts.aisle', 'Aisle')} fullWidth required select value={parsedName?.aisle} disabled>
                    <MenuItem value={parsedName?.aisle}>{parsedName?.aisle}</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={4}>
                <TextField required label={t('overseer_app.layouts.aisle_orientation', 'Orientation')} fullWidth value={parsedName?.orientation} select disabled>
                    {ORIENTATIONS.map(orientation => (
                        <MenuItem value={orientation} key={orientation} >{orientation}</MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={5}>
                <TextField label={t('overseer_app.layouts.aisle_part', 'Aisle Part')} fullWidth value={parsedName?.part || ''} disabled />
            </Grid>
            {zoneCollisions?.length > 0 && (
                <Grid item xs={12}>
                    {/* Hint only when there are several zones and the default
                        (largest overlap, [0]) is still selected. */}
                    {zoneCollisions.length > 1 && formState?.zone === zoneCollisions[0]?.layer && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            {t('overseer_app.layouts.creation.zone_default_helper', 'This is the zone with the largest overlap. You can pick another one.')}
                        </Alert>
                    )}
                    <TextField label={t('overseer_app.layouts.zone', 'Zone')} fullWidth select value={formState?.zone} onChange={(e) => handleFieldChange('zone', e.target.value)}>
                        {zoneCollisions.map(({ layer, color }) => (
                            <MenuItem value={layer} key={layer}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    {layer}
                                    <CircleColor color={color} />
                                </Box>
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            )}
            <Grid item xs={12}>
                <TextField label={t('overseer_app.layouts.creation.linear_client_name', 'Client\'s linear name')} fullWidth value={formState?.clientName} onChange={(e) => handleFieldChange('clientName', e.target.value)} error={!!errors.clientName}
                    helperText={
                        errors?.clientName ||
                        t('overseer_app.layouts.helper.client_name_helper', 'Only letters, numbers, underscores (_), and hyphens (-) are allowed.')}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl component="fieldset" required error={!!errors.linearType}>
                    <FormLabel component="legend">{t('overseer_app.layouts.aisle_type', 'Aisle type')}</FormLabel>
                    {isLoadingLinearTypes ? (
                        <CircularLoader size="2em" />
                    ) : !linearTypes.length ? (
                        <FormHelperText error>
                            {t('overseer_app.layouts.linear_types_error', 'Aisle types could not be loaded. Close and reopen this dialog to try again.')}
                        </FormHelperText>
                    ) : (
                        <RadioGroup
                            row
                            name="linearType"
                            value={formState.linearType}
                            onChange={(e) => handleFieldChange('linearType', e.target.value)}
                        >
                            {linearTypes.map((value) => (
                                <FormControlLabel key={value} value={value} control={<Radio />} label={t(`overseer_app.layouts.linear_types.${value}`, value)} />
                            ))}
                        </RadioGroup>
                    )}
                    {typeParamError && (
                        <FormHelperText sx={{ color: 'warning.main' }}>
                            {typeParamError.kind === 'missing_param'
                                ? t('overseer_app.layouts.linear_type_param_required',
                                    'Parameters must include "{{key}}: true" for the selected type.',
                                    { key: linearTypeParams[formState.linearType]?.key })
                                : t('overseer_app.layouts.linear_type_param_conflict',
                                    'Parameters contain "{{key}}: true" which belongs to type "{{type}}". Update the type selector or remove the parameter.',
                                    { key: linearTypeParams[typeParamError.foreignType]?.key, type: typeParamError.foreignType })
                            }
                        </FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <TextField multiline minRows={2} label={t('overseer_app.layouts.parameters', 'Parameters')} fullWidth value={formState?.params} onChange={(e) => handleFieldChange('params', e.target.value)} error={!!errors.params} helperText={t('overseer_app.layouts.creation.parameters_helper', 'This field must be a valid JSON object')} />
            </Grid>

        </Grid>
    )
})